import React, { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import MainContentWrapper from "../../Components/MainContentWrapper";
import Popup from "../../ReusableFunctions/Popup";
import { Button } from "../../Components/Helper";
import DownloadIcon from "../../assets/Download.png";
import calender from "../../assets/blueCalendar.svg";
import {
  downloadOrderPerformanceReportExcel,
  downloadPilotsPerformanceReportExcel,
  getOrderPerformanceReport,
  getPilotsPerformanceReport,
} from "../../apis/reports";
import TableView from "../../ReusableComponents/TableView/TableView";
import { useSelector } from "react-redux";

const periodOptions = [
  { key: "day", label: "Today" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "year", label: "This Year" },
  { key: "custom", label: "Custom" },
];

const startEndForPeriod = (periodKey) => {
  const now = new Date();
  const todayEnd = new Date(now);
  todayEnd.setHours(23, 59, 59, 999);
  let fromDate = new Date();
  let toDate = new Date();

  switch (periodKey) {
    case "day":
      fromDate.setHours(0, 0, 0, 0);
      toDate.setHours(23, 59, 59, 999);
      break;
    case "week": {
      const day = now.getDay(); // 0 Sunday - 6 Saturday
      fromDate = new Date(now);
      fromDate.setDate(now.getDate() - (day === 0 ? 6 : day - 1)); // Monday
      fromDate.setHours(0, 0, 0, 0);
      toDate = new Date(fromDate);
      toDate.setDate(fromDate.getDate() + 6); // Sunday
      toDate.setHours(23, 59, 59, 999);
      break;
    }
    case "month":
      fromDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
      toDate = new Date(
        now.getFullYear(),
        now.getMonth() + 1,
        0,
        23,
        59,
        59,
        999,
      );
      break;
    case "year":
      fromDate = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
      toDate = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
      break;
    default:
      fromDate.setHours(0, 0, 0, 0);
      toDate.setHours(23, 59, 59, 999);
      break;
  }

  // Disallow future dates: clamp end date to today (end of day)
  if (toDate.getTime() > todayEnd.getTime()) toDate = todayEnd;

  return { fromDate, toDate };
};

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

const OrderPerformanceReport = () => {
  const initial = useMemo(() => startEndForPeriod("day"), []);
  const [period, setPeriod] = useState("day");
  const [lastPresetPeriod, setLastPresetPeriod] = useState("day");
  const [fromDate, setFromDate] = useState(initial.fromDate);
  const [toDate, setToDate] = useState(initial.toDate);
  const today = useMemo(() => new Date(), []);
  const [openCustomPopover, setOpenCustomPopover] = useState(false);
  const [customFrom, setCustomFrom] = useState(initial.fromDate);
  const [customTo, setCustomTo] = useState(initial.toDate);
  const [openCustomDatePicker, setOpenCustomDatePicker] = useState({
    from: false,
    to: false,
  });
    const user = useSelector((state) => state.user.user);

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [tableState, setTableState] = useState({
    pageSize: 10,
    pageNumber: 1,
    // orderBy: "pilot_name",
    orderBy: "date_created",
    orderType: "ASC",
  });
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const fetchReport = async ({
    from,
    to,
    pageNumber,
    pageSize,
    orderBy,
    orderType,
  }) => {
    try {
      console.log("from:", from, "to:", to);
      setLoading(true);
      const data = await getOrderPerformanceReport({
        fromDate: from.toISOString(),
        toDate: to.toISOString(),
        pageNumber,
        pageSize,
        orderBy,
        orderType,
      });
      const normalized = (data?.data || []).map((r) => {
        const acres = r?.acre_service_completed_area || null;
        const tanks = r?.tank_service_completed_area || null;

        let completedArea = "-";
        if (acres && tanks) {
          completedArea = `${acres} ${user?.dsp?.settings?.units}/${tanks} Tanks`;
        } else if (acres) {
          completedArea = `${acres} ${user?.dsp?.settings?.units}`;
        } else if (tanks) {
          completedArea = `${tanks} Tanks`;
        }

        const formatDate = (dateStr) => {
          if (!dateStr) return "-";
          const date = new Date(dateStr);
          const day = String(date.getDate()).padStart(2, "0");
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const year = date.getFullYear();
          return `${day}/${month}/${year}`;
        };

        const formatDateAndTime = (dateStr) => {
          if (!dateStr) return "-";
          const date = new Date(dateStr);
          const day = String(date.getDate()).padStart(2, "0");
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const year = date.getFullYear();
          const time = dateStr.split("T")[1]; // 07:58:14.174Z
          return `${day}/${month}/${year} ${time}`;
        };

        return {
          ...r,
          date_created: formatDate(r.date_created),
          time: formatDate(r.time),
          completed_area: completedArea,
        };
      });
      setRows(normalized);
      setCount(Number(data?.count || 0));
      setLoading(false);
    } catch (e) {
      setLoading(false);
      Popup.alert("Error", e?.message || "Failed to load report.", "ERROR");
    }
  };

  useEffect(() => {
    if (period === "custom") return;
    setLastPresetPeriod(period);
    const range = startEndForPeriod(period);
    setFromDate(range.fromDate);
    setToDate(range.toDate);
    setTableState((prev) => ({ ...prev, pageNumber: 1 }));
    fetchReport({
      from: range.fromDate,
      to: range.toDate,
      ...tableState,
      pageNumber: 1,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  useEffect(() => {
    // initial load
    fetchReport({ from: initial.fromDate, to: initial.toDate, ...tableState });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDownload = async () => {
  try {
    setDownloading(true);
    const blob = await downloadOrderPerformanceReportExcel({
      fromDate: fromDate.toISOString(),
      toDate: toDate.toISOString(),
      orderBy: tableState.orderBy,
      orderType: tableState.orderType,
    });
    const safeFrom = fromDate.toISOString().slice(0, 10);
    const safeTo = toDate.toISOString().slice(0, 10);
    downloadBlob(blob, `order_report_${safeFrom}_to_${safeTo}.xlsx`);
    setDownloading(false);
  } catch (e) {
    setDownloading(false);
    Popup.alert("Error", e?.message || "Failed to download report.", "ERROR");
  }
};
  const openCustom = () => {
    setCustomFrom(fromDate);
    setCustomTo(toDate);
    setOpenCustomPopover(true);
  };

  const closeCustom = () => {
    setOpenCustomPopover(false);
    setOpenCustomDatePicker({ from: false, to: false });
  };

  const applyCustom = () => {
    if (!customFrom || !customTo) return;
    if (customFrom.getTime() > customTo.getTime()) {
      return Popup.alert(
        "Reports",
        "From date cannot be after To date.",
        "ERROR",
      );
    }
    setFromDate(customFrom);
    setToDate(customTo);
    setPeriod("custom");
    setTableState((prev) => ({ ...prev, pageNumber: 1 }));
    fetchReport({
      from: customFrom,
      to: customTo,
      ...tableState,
      pageNumber: 1,
    });
    setOpenCustomPopover(false);
  };

  const cancelCustom = () => {
    setOpenCustomPopover(false);
    setOpenCustomDatePicker({ from: false, to: false });
    // revert toggle to previous preset period
    setPeriod(lastPresetPeriod || "day");
  };

  return (
    <MainContentWrapper
      header={"Spraying Orders Report"}
      rightContent={
        <Button
          text={"Download Report"}
          // icon={DownloadIcon}
          onClick={onDownload}
          isLoading={downloading}
          LoadingText={"Downloading..."}
          style={{
            height: "35px",
            width: "160px",
            fontSize: "14px",
            fontWeight: 500,
          }}
          isDisabled={loading || !fromDate || !toDate}
        />
      }
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: "12px",
          padding: "0px 0px 12px 0px",
          borderBottom: "1px solid #BFC8D9",
        }}
      >
        {/* Left: current applied date range */}
        <div style={{ fontSize: "12px", color: "#3B3B3B", fontWeight: 600 }}>
          {fromDate ? fromDate.toLocaleDateString("en-GB") : "--/--/----"} -{" "}
          {toDate ? toDate.toLocaleDateString("en-GB") : "--/--/----"}
        </div>

        {/* Right: range selector */}
        <div
          className="duration_selectors"
          style={{ fontSize: "14px", width: "auto" }}
        >
          {periodOptions.map((p, i) => {
            const isCustomBtn = p.key === "custom";
            const btn = (
              <button
                key={p.key}
                style={{
                  borderLeft:
                    i === 0 && period !== p.key && "1px solid #C9C9C9",
                  borderRight: period !== p.key && "1px solid #C9C9C9",
                }}
                className={period === p.key ? "active" : ""}
                onClick={() => {
                  if (p.key === "custom") openCustom();
                  else {
                    closeCustom();
                    setPeriod(p.key);
                  }
                }}
              >
                {p.label}
              </button>
            );

            if (!isCustomBtn) return btn;

            return (
              <div
                key="custom-wrapper"
                style={{ position: "relative", display: "inline-block" }}
              >
                {btn}

                {openCustomPopover && (
                  <div
                    style={{
                      position: "absolute",
                      right: 0,
                      top: "42px",
                      zIndex: 1000,
                      background: "#FFFFFF",
                      border: "1px solid rgba(191, 200, 217, 0.8)",
                      borderRadius: "12px",
                      padding: "12px",
                      boxShadow: "0px 8px 24px rgba(0,0,0,0.12)",
                      minWidth: "420px",
                    }}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "12px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "6px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "12px",
                            fontWeight: 600,
                            color: "#3B3B3B",
                          }}
                        >
                          From
                        </div>
                        <div
                          className="input-date"
                          style={{
                            backgroundColor: "white",
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            position: "relative",
                            height: "35px",
                            border: "1.4px solid rgba(217, 217, 217, 0.84)",
                            borderRadius: "8px",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            setOpenCustomDatePicker((prev) => ({
                              ...prev,
                              from: true,
                              to: false,
                            }))
                          }
                        >
                          <DatePicker
                            onClickOutside={() =>
                              setOpenCustomDatePicker((prev) => ({
                                ...prev,
                                from: false,
                              }))
                            }
                            open={openCustomDatePicker.from}
                            dateFormat="dd/MM/yyyy"
                            className="input-date-hidden"
                            selected={customFrom}
                            onSelect={() =>
                              setOpenCustomDatePicker((prev) => ({
                                ...prev,
                                from: false,
                              }))
                            }
                            onChange={(d) => {
                              if (!d) return;
                              const fd = new Date(d);
                              fd.setHours(0, 0, 0, 0);
                              setCustomFrom(fd);
                              if (
                                customTo &&
                                fd.getTime() > customTo.getTime()
                              ) {
                                const td = new Date(fd);
                                td.setHours(23, 59, 59, 999);
                                setCustomTo(td);
                              }
                              setOpenCustomDatePicker((prev) => ({
                                ...prev,
                                from: false,
                              }));
                            }}
                            maxDate={
                              customTo
                                ? customTo.getTime() < today.getTime()
                                  ? customTo
                                  : today
                                : today
                            }
                            readOnly
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            popperClassName="reports-datepicker-popper"
                            popperPlacement="bottom-start"
                          />
                          <img
                            src={calender}
                            alt="cal"
                            style={{
                              width: "18px",
                              position: "absolute",
                              right: "12px",
                            }}
                          />
                        </div>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "6px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "12px",
                            fontWeight: 600,
                            color: "#3B3B3B",
                          }}
                        >
                          To
                        </div>
                        <div
                          className="input-date"
                          style={{
                            backgroundColor: "white",
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            position: "relative",
                            height: "35px",
                            border: "1.4px solid rgba(217, 217, 217, 0.84)",
                            borderRadius: "8px",
                            cursor: customFrom ? "pointer" : "not-allowed",
                            opacity: customFrom ? 1 : 0.6,
                          }}
                          onClick={() => {
                            if (!customFrom) return;
                            setOpenCustomDatePicker((prev) => ({
                              ...prev,
                              to: true,
                              from: false,
                            }));
                          }}
                        >
                          <DatePicker
                            onClickOutside={() =>
                              setOpenCustomDatePicker((prev) => ({
                                ...prev,
                                to: false,
                              }))
                            }
                            open={openCustomDatePicker.to}
                            dateFormat="dd/MM/yyyy"
                            className="input-date-hidden"
                            selected={customTo}
                            onSelect={() =>
                              setOpenCustomDatePicker((prev) => ({
                                ...prev,
                                to: false,
                              }))
                            }
                            onChange={(d) => {
                              if (!d) return;
                              const td = new Date(d);
                              td.setHours(23, 59, 59, 999);
                              setCustomTo(td);
                              if (
                                customFrom &&
                                customFrom.getTime() > td.getTime()
                              ) {
                                const fd = new Date(td);
                                fd.setHours(0, 0, 0, 0);
                                setCustomFrom(fd);
                              }
                              setOpenCustomDatePicker((prev) => ({
                                ...prev,
                                to: false,
                              }));
                            }}
                            minDate={customFrom || undefined}
                            maxDate={today}
                            disabled={!customFrom}
                            readOnly
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            popperClassName="reports-datepicker-popper"
                            popperPlacement="bottom-start"
                          />
                          <img
                            src={calender}
                            alt="cal"
                            style={{
                              width: "18px",
                              position: "absolute",
                              right: "12px",
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: "10px",
                        marginTop: "12px",
                      }}
                    >
                      <Button
                        text={"Cancel"}
                        onClick={cancelCustom}
                        className={"btn-large lg-cancel-btn"}
                        isBtnNotActive={true}
                      />
                      <Button
                        text={"Apply"}
                        onClick={applyCustom}
                        className={"btn-large"}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <TableView
        isLoading={loading}
        hidePagination={false}
        loadingHeight={"55vh"}
        tableContainerHeight={"681px"}
        pageSize={10}
        pageNumber={tableState.pageNumber}
        columns={[
          {
            Header: "Request No",
            accessor: "req_number",
            width: "9%",
            isSortable: true,
          },
          {
            Header: "Request Date",
            accessor: "date_created",
            width: "9%",
            isSortable: true,
          },
          {
            Header: "Service Date",
            accessor: "time",
            width: "9%",
            isSortable: true,
            isTextCenter: true,
          },
          {
            Header: "Location",
            accessor: "location_name",
            width: "7%",
            isSortable: true,
            isTextCenter: true,
          },
          {
            Header: "Brief Address",
            accessor: "field_address",
            width: "9%",
            isSortable: true,
            isTextCenter: true,
          },
          {
            Header: "Customer Name",
            accessor: "customer_name",
            width: "9%",
            isSortable: true,
            isTextCenter: true,
          },
          {
            Header: "Pilots Name",
            accessor: "pilot_name",
            width: "8%",
            isSortable: true,
            isTextCenter: true,
          },
          {
            Header: `Completed Area ${user?.dsp?.settings?.units}/No. of Tanks`,
            accessor: "completed_area",
            width: "10%",
            isSortable: false,
            isTextCenter: true,
          },
          {
            Header: `Rate per ${user?.dsp?.settings?.units}/per Tank`,
            accessor: "rate",
            width: "7%",
            isSortable: true,
            isTextCenter: true,
          },
          {
            Header: "Amount",
            accessor: "amount",
            width: "6%",
            isSortable: true,
            isTextCenter: true,
          },
          {
            Header: "Status",
            accessor: "status",
            width: "9%",
            isSortable: true,
            isTextCenter: true,
            noWrap: true,
          },
        ]}
        data={rows}
        count={count}
        orderBy={tableState.orderBy}
        orderType={tableState.orderType}
        onStateChange={(state) => {
          const next = {
            pageSize: Number(state?.pageSize || 10),
            pageNumber: Number(state?.pageNumber || 1),
            orderBy: state?.orderBy || tableState.orderBy,
            orderType: state?.orderType || tableState.orderType,
          };
          setTableState(next);
          fetchReport({ from: fromDate, to: toDate, ...next });
        }}
      />
    </MainContentWrapper>
  );
};

export default OrderPerformanceReport;
